import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

export default function AdminPos() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [envName, setEnvName] = useState('SANDBOX');
  const [posProvider, setPosProvider] = useState('SQUARE');
  const [posStatus, setPosStatus] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [injectResult, setInjectResult] = useState(null);
  const [injectVariationId, setInjectVariationId] = useState('');
  const [syncedProducts, setSyncedProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [notifyEmailsText, setNotifyEmailsText] = useState('');
  const [notifyPhonesText, setNotifyPhonesText] = useState('');

  const [posTaxEnabled, setPosTaxEnabled] = useState(false);
  const [posTaxCollectionMode, setPosTaxCollectionMode] = useState('PROVIDER');
  const [posTaxRatePercent, setPosTaxRatePercent] = useState('8.25');

  // Square Terminal API pairing (card-present collection for unpaid PAY_AT_POS tickets).
  // Additive: renders only for Square, and every piece of state is empty/false until the
  // owner actually starts a pairing, so Clover/Stripe tenants see no change at all.
  const [terminalPaired, setTerminalPaired] = useState(false);
  const [terminalDeviceId, setTerminalDeviceId] = useState('');
  const [terminalPairCode, setTerminalPairCode] = useState('');
  const [terminalPairPolling, setTerminalPairPolling] = useState(false);
  const terminalPairPollRef = useRef(null);

  // Clover Terminal Charge (clover-terminal-charge) pairing. Additive and Clover-only:
  // REST Pay Display uses a cloud connection with NO pairing code — the admin enters the
  // device serial (shown in the Clover device Setup app / web dashboard). RAID is optional
  // (defaults to the tenant-wide CLOVER_REMOTE_APP_ID). All state is empty until a Clover
  // tenant pairs, so Square/Stripe tenants see nothing new.
  const [cloverTerminalPaired, setCloverTerminalPaired] = useState(false);
  const [cloverTerminalDeviceId, setCloverTerminalDeviceId] = useState('');
  const [cloverPairDeviceInput, setCloverPairDeviceInput] = useState('');
  const [cloverPairRaidInput, setCloverPairRaidInput] = useState('');

  const isStripe = String(posProvider || '').trim().toUpperCase() === 'STRIPE';
  const isSquare = String(posProvider || '').trim().toUpperCase() === 'SQUARE';
  const isClover = String(posProvider || '').trim().toUpperCase() === 'CLOVER';

  const redirectUri = useMemo(() => {
    try {
      return `${window.location.origin}/admin/channels/pos/callback`;
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'General Settings');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');

        let resolvedProvider = posProvider;
        try {
          const token = session.getIdToken().getJwtToken();
          const generalRes = await fetch(`${API_BASE_URL}/admin/general`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const generalData = await generalRes.json().catch(() => ({}));
          if (generalRes.ok) {
            const p = String(generalData?.settings?.posProvider ?? generalData?.posProvider ?? '')
              .trim()
              .toUpperCase();
            if (p === 'CLOVER' || p === 'SQUARE' || p === 'STRIPE') {
              resolvedProvider = p;
              setPosProvider(p);
              if (p === 'STRIPE') {
                setEnvName('STRIPE');
              }
            }
          }
        } catch {
        }
        await fetchStatus(envName, resolvedProvider);
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchSyncedProducts = async () => {
    if (!API_BASE_URL) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load products');

      const products = Array.isArray(data?.products) ? data.products : [];
      const synced = products.filter(
        (p) => p?.SquareCatalogVariationId && p?.SquareCatalogItemId
      );
      setSyncedProducts(synced);
      if (!selectedProductId && synced.length > 0) {
        setSelectedProductId(String(synced[0].ProductID || ''));
      }
    } catch (e) {
      setSyncedProducts([]);
      setError(e?.message || 'Failed to load products');
    }
  };

  useEffect(() => {
    if (loading) return;
    fetchStatus(envName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, envName, posProvider]);

  useEffect(() => {
    if (loading) return;
    if (!posStatus?.connected) return;
    if (isStripe) return;
    fetchLocations(envName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envName, posProvider, posStatus?.connected, isStripe]);

  useEffect(() => {
    if (loading) return;
    if (!posStatus?.connected) return;
    if (isStripe) return;
    fetchSyncedProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posStatus?.connected, isStripe]);

  const fetchStatus = async (env, providerOverride) => {
    if (!API_BASE_URL) return;
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const effectiveProvider = String(providerOverride || posProvider || 'SQUARE').trim().toUpperCase() || 'SQUARE';
      const isStripeProvider = effectiveProvider === 'STRIPE';

      const qs = new URLSearchParams();
      qs.set('environment', String(env || ''));
      qs.set('provider', effectiveProvider);

      const res = await fetch(`${API_BASE_URL}/admin/pos/status?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load POS status');
      setPosStatus(data);

      if (data?.posNotifyEnabled !== undefined && data?.posNotifyEnabled !== null) {
        setNotifyEnabled(Boolean(data.posNotifyEnabled));
      }
      if (Array.isArray(data?.posNotifyEmails)) {
        setNotifyEmailsText(data.posNotifyEmails.filter(Boolean).join(', '));
      } else if (typeof data?.posNotifyEmails === 'string') {
        setNotifyEmailsText(String(data.posNotifyEmails || ''));
      }
      if (Array.isArray(data?.posNotifyPhones)) {
        setNotifyPhonesText(data.posNotifyPhones.filter(Boolean).join(', '));
      } else if (typeof data?.posNotifyPhones === 'string') {
        setNotifyPhonesText(String(data.posNotifyPhones || ''));
      }

      if (data?.posLocationId) {
        setSelectedLocationId(String(data.posLocationId));
      }

      // Terminal pairing state. posTerminalPaired is returned for every viewer;
      // posTerminalDeviceId is admin-only, so it can legitimately be absent.
      setTerminalPaired(Boolean(data?.posTerminalPaired));
      setTerminalDeviceId(String(data?.posTerminalDeviceId || ''));

      if (data?.posTaxEnabled !== undefined && data?.posTaxEnabled !== null) {
        setPosTaxEnabled(Boolean(data.posTaxEnabled));
      }
      if (data?.posTaxCollectionMode) {
        const m = String(data.posTaxCollectionMode || '').trim().toUpperCase();
        if (isStripeProvider) {
          setPosTaxCollectionMode('OWN');
        } else if (m === 'PROVIDER' || m === 'OWN') {
          setPosTaxCollectionMode(m);
        }
      }
      if (data?.posTaxRatePercent !== undefined && data?.posTaxRatePercent !== null) {
        const v = String(data.posTaxRatePercent);
        if (v.trim()) setPosTaxRatePercent(v);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load POS status');
      setPosStatus(null);
      setLocations([]);
      setTerminalPaired(false);
      setTerminalDeviceId('');
    }
  };

  // ---------------------------------------------------------------------------
  // Square Terminal pairing
  //
  // Square will not show an UNPAID order created over the API on any of its own
  // surfaces, so a cashier cannot ring up a PAY_AT_POS ticket from the Terminal's
  // own screen. The Terminal API is the sanctioned path: pair a device once here,
  // then the POS Queue can push a checkout to it. Pairing is a one-time config
  // step per location + environment.
  // ---------------------------------------------------------------------------
  const stopTerminalPairPolling = () => {
    setTerminalPairPolling(false);
    if (terminalPairPollRef.current) {
      try {
        window.clearInterval(terminalPairPollRef.current);
      } catch {
        // ignore
      }
      terminalPairPollRef.current = null;
    }
  };

  useEffect(() => stopTerminalPairPolling, []);

  const pollTerminalPairOnce = async (codeId) => {
    if (!API_BASE_URL) return false;
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    const qs = new URLSearchParams();
    qs.set('environment', String(envName || ''));
    if (codeId) qs.set('device_code_id', String(codeId));

    const res = await fetch(`${API_BASE_URL}/admin/pos/terminal/device-status?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to read Terminal pairing status'));

    if (data?.paired && data?.device_id) {
      setTerminalPaired(true);
      setTerminalDeviceId(String(data.device_id));
      setTerminalPairCode('');
      setStatusMessage('Square Terminal paired. It can now take pushed checkouts.');
      return true;
    }
    return false;
  };

  const startTerminalPairing = async () => {
    if (!API_BASE_URL) return;
    if (!isSquare) return;
    if (busy) return;

    stopTerminalPairPolling();
    setBusy(true);
    try {
      setError('');
      setStatusMessage('');
      setTerminalPairCode('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/terminal/device-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environment: envName,
          location_id: selectedLocationId,
          name: `${PROJECT_NAME} Terminal`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to create Terminal pairing code'));

      const code = String(data?.code || '').trim();
      const codeId = String(data?.device_code_id || '').trim();
      if (!code) throw new Error('Square did not return a pairing code');

      setTerminalPairCode(code);
      setStatusMessage('Enter this code on the Square Terminal. Waiting for the device…');

      // Square device codes are short-lived, so poll until paired or the owner stops.
      setTerminalPairPolling(true);
      terminalPairPollRef.current = window.setInterval(async () => {
        try {
          const done = await pollTerminalPairOnce(codeId);
          if (done) stopTerminalPairPolling();
        } catch (e) {
          stopTerminalPairPolling();
          setError(e?.message || 'Failed to read Terminal pairing status');
        }
      }, 3000);
    } catch (e) {
      setError(e?.message || 'Failed to create Terminal pairing code');
    } finally {
      setBusy(false);
    }
  };

  const unpairTerminal = async () => {
    if (!API_BASE_URL) return;
    if (!isSquare) return;
    if (busy) return;
    if (!window.confirm('Forget the paired Square Terminal? Card-present charges from the POS Queue will stop working until a device is paired again.')) {
      return;
    }

    stopTerminalPairPolling();
    setBusy(true);
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/terminal/unpair`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: envName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to unpair Terminal'));

      setTerminalPaired(false);
      setTerminalDeviceId('');
      setTerminalPairCode('');
      setStatusMessage('Square Terminal unpaired.');
    } catch (e) {
      setError(e?.message || 'Failed to unpair Terminal');
    } finally {
      setBusy(false);
    }
  };

  // ---- CLOVER Terminal Charge pairing (clover-terminal-charge) ------------------------
  const loadCloverTerminalPairing = async () => {
    if (!API_BASE_URL) return;
    if (!isClover) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/pos/clover/terminal/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setCloverTerminalPaired(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      setCloverTerminalPaired(Boolean(data?.paired));
      setCloverTerminalDeviceId(String(data?.device_id || ''));
      if (data?.remote_app_id && !cloverPairRaidInput) {
        setCloverPairRaidInput(String(data.remote_app_id));
      }
    } catch {
      setCloverTerminalPaired(false);
    }
  };

  useEffect(() => {
    loadCloverTerminalPairing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClover]);

  const pairCloverTerminal = async () => {
    if (!API_BASE_URL) return;
    if (!isClover) return;
    if (busy) return;

    const deviceId = String(cloverPairDeviceInput || '').trim();
    if (!deviceId) {
      setError('Enter the Clover device serial to pair.');
      return;
    }

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/clover/terminal/pair`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environment: envName,
          device_id: deviceId,
          ...(String(cloverPairRaidInput || '').trim() ? { remote_app_id: String(cloverPairRaidInput).trim() } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to pair Clover terminal'));

      setCloverTerminalPaired(true);
      setCloverTerminalDeviceId(deviceId);
      setCloverPairDeviceInput('');
      if (data?.reachable === false) {
        setStatusMessage(
          'Clover terminal paired, but the device did not answer a ping. It will still work once it is online and on the Cloud Pay Display app.'
        );
      } else {
        setStatusMessage('Clover terminal paired. It can now take charges pushed from the POS Queue.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to pair Clover terminal');
    } finally {
      setBusy(false);
    }
  };

  const unpairCloverTerminal = async () => {
    if (!API_BASE_URL) return;
    if (!isClover) return;
    if (busy) return;
    if (!window.confirm('Forget the paired Clover terminal? Card-present charges from the POS Queue will stop working until a device is paired again.')) {
      return;
    }

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/clover/terminal/unpair`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: envName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to unpair Clover terminal'));

      setCloverTerminalPaired(false);
      setCloverTerminalDeviceId('');
      setStatusMessage('Clover terminal unpaired.');
    } catch (e) {
      setError(e?.message || 'Failed to unpair Clover terminal');
    } finally {
      setBusy(false);
    }
  };

  const savePosTaxes = async () => {
    if (!API_BASE_URL) return;
    if (busy) return;

    if (isStripe && String(posTaxCollectionMode || '').toUpperCase() === 'PROVIDER') {
      setPosTaxCollectionMode('OWN');
    }

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        provider: posProvider,
        environment: envName,
        posTaxEnabled: Boolean(posTaxEnabled),
        posTaxCollectionMode: String(isStripe ? 'OWN' : posTaxCollectionMode || 'PROVIDER').toUpperCase(),
      };

      const effectiveMode = String(payload.posTaxCollectionMode || 'PROVIDER').toUpperCase();
      payload.posTaxCollectionMode = effectiveMode;

      if (posTaxEnabled && effectiveMode === 'OWN') {
        payload.posTaxRatePercent = String(posTaxRatePercent || '').trim();
      }

      const res = await fetch(`${API_BASE_URL}/admin/pos/taxes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to save POS tax settings'));

      setStatusMessage('POS tax settings saved.');
      await fetchStatus(envName);
    } catch (e) {
      setError(e?.message || 'Failed to save POS tax settings');
    } finally {
      setBusy(false);
    }
  };

  const saveNotifications = async () => {
    if (!API_BASE_URL) return;
    if (busy) return;

    const normalizeCsv = (txt) => {
      const s = String(txt || '').trim();
      if (!s) return [];
      return s
        .split(',')
        .map((p) => String(p || '').trim())
        .filter(Boolean);
    };

    const emails = normalizeCsv(notifyEmailsText);
    const phones = normalizeCsv(notifyPhonesText);

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/notifications`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: posProvider,
          environment: envName,
          posNotifyEnabled: Boolean(notifyEnabled),
          posNotifyEmails: emails,
          posNotifyPhones: phones,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to save notification settings'));

      setStatusMessage('Restaurant notification settings saved.');
      await fetchStatus(envName);
    } catch (e) {
      setError(e?.message || 'Failed to save notification settings');
    } finally {
      setBusy(false);
    }
  };

  const injectTestOrder = async () => {
    if (!API_BASE_URL) return;
    if (busy) return;
    if (isStripe) return;
    if (!posStatus?.connected) return;
    if (!selectedLocationId) return;

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');
      setInjectResult(null);

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const selectedProduct = syncedProducts.find(
        (p) => String(p?.ProductID || '') === String(selectedProductId || '')
      );
      const selectedVariationId = String(selectedProduct?.SquareCatalogVariationId || '').trim();
      const trimmedVariationId = String(injectVariationId || selectedVariationId || '').trim();
      const items = [
        {
          name: 'Test Item',
          quantity: 1,
          price_cents: 100,
          currency: 'USD',
          ...(trimmedVariationId ? { SquareCatalogVariationId: trimmedVariationId } : {}),
        },
      ];

      const res = await fetch(`${API_BASE_URL}/admin/pos/order/inject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: posProvider,
          environment: envName,
          username,
          posLocationId: selectedLocationId,
          notes: 'Admin POS test order',
          items,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to inject test order'));

      setInjectResult(data);
      if (data?.injected) {
        setStatusMessage(
          posProvider === 'CLOVER'
            ? 'Test order injected into Clover. Check Clover Sandbox dashboard orders.'
            : 'Test order injected into Square. Check Square Sandbox dashboard orders.'
        );
      } else {
        setError(data?.reason || 'Test order was not injected.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to inject test order');
    } finally {
      setBusy(false);
    }
  };

  const fetchLocations = async (env) => {
    if (!API_BASE_URL) return;
    if (isStripe) return;
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const qs = new URLSearchParams();
      qs.set('environment', String(env || ''));
      qs.set('provider', String(posProvider || 'SQUARE'));

      const res = await fetch(`${API_BASE_URL}/admin/pos/locations?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load POS locations');

      const locs = Array.isArray(data?.locations) ? data.locations : [];
      setLocations(locs);

      if (data?.posLocationId) {
        setSelectedLocationId(String(data.posLocationId));
      }
    } catch (e) {
      setLocations([]);
      setError(e?.message || 'Failed to load POS locations');
    }
  };

  const saveLocation = async (locationId) => {
    if (!API_BASE_URL) return;
    if (!locationId) return;
    if (busy) return;
    if (isStripe) return;

    setBusy(true);
    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/location`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ provider: posProvider, environment: envName, location_id: locationId }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to save POS location'));

      setSelectedLocationId(locationId);
      setStatusMessage('POS location saved.');
      await fetchStatus(envName);
    } catch (e) {
      setError(e?.message || 'Failed to save POS location');
    } finally {
      setBusy(false);
    }
  };

  const startConnect = async () => {
    if (!API_BASE_URL) return;
    if (busy) return;
    if (isStripe) return;
    setBusy(true);

    const popup = (() => {
      try {
        return window.open('about:blank', '_blank');
      } catch {
        return null;
      }
    })();

    try {
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/connect/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: posProvider,
          environment: envName,
          redirect_uri: redirectUri,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to start POS connect'));

      const returnedState = String(data?.state || '').trim();
      if (returnedState) {
        try {
          const keyPrefix = posProvider === 'CLOVER' ? 'clover' : 'square';
          window.localStorage?.setItem(`${keyPrefix}_oauth_env_${returnedState}`, String(envName || '').trim());
        } catch {
        }
      }

      const url = String(data?.authorize_url || '').trim();
      if (url) {
        if (popup && !popup.closed) {
          try {
            try {
              popup.opener = null;
            } catch {
            }
            popup.location.href = url;

            window.setTimeout(() => {
              try {
                if (!popup || popup.closed) return;
                const href = String(popup.location?.href || '');
                if (href === 'about:blank') {
                  try {
                    popup.close();
                  } catch {
                  }
                  window.location.assign(url);
                }
              } catch {
              }
            }, 600);
          } catch {
            try {
              popup.close();
            } catch {
            }
            window.location.assign(url);
          }
        } else {
          window.location.assign(url);
        }
        setStatusMessage(
          `${posProvider === 'CLOVER' ? 'Clover' : 'Square'} connect opened in a new tab. Complete authorization, then return here.`
        );
      } else {
        try {
          if (popup && !popup.closed) popup.close();
        } catch {
        }
        setStatusMessage('Connect started, but no authorization URL was returned.');
      }
    } catch (e) {
      try {
        if (popup && !popup.closed) popup.close();
      } catch {
      }
      setError(e?.message || 'Failed to start POS connect');
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    await fetchStatus(envName);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-cash-register"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>POS ({posProvider === 'CLOVER' ? 'Clover' : posProvider === 'STRIPE' ? 'Stripe' : 'Square'})</h1>
            <p>
              {posProvider === 'STRIPE'
                ? 'Configure POS taxes for Stripe Checkout.'
                : `Connect ${posProvider === 'CLOVER' ? 'Clover' : 'Square'}, configure location, and manage notifications`}
            </p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

          <div className="products-list-container">
          {!isStripe ? (
            <div className="pos-toolbar">
              <div className="pos-toolbar-group">
                <label className="pos-toolbar-label">Environment</label>
                <select className="pos-input" value={envName} onChange={(e) => setEnvName(e.target.value)} disabled={busy}>
                  <option value="SANDBOX">SANDBOX</option>
                  <option value="PRODUCTION">PRODUCTION</option>
                </select>
              </div>

              <div className="pos-toolbar-group">
                <label className="pos-toolbar-label">Location</label>
                <select
                  className="pos-input"
                  value={selectedLocationId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSelectedLocationId(next);
                    saveLocation(next);
                  }}
                  disabled={!posStatus?.connected || locations.length === 0 || busy}
                >
                  <option value="">Select…</option>
                  {locations.map((l) => (
                    <option key={String(l?.id || '')} value={String(l?.id || '')}>
                      {String(l?.name || l?.id || '')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pos-toolbar-actions">
                <button type="button" className="btn-secondary" onClick={refresh} disabled={busy}>
                  Refresh status
                </button>
                <button type="button" className="btn-primary" onClick={startConnect} disabled={busy || !redirectUri}>
                  {posProvider === 'CLOVER' ? 'Connect Clover' : 'Connect Square'}
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={injectTestOrder}
                  disabled={busy || !posStatus?.connected || !selectedLocationId}
                >
                  Inject test order
                </button>
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 18, display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
            {!isStripe ? (
              <div className="pos-panel">
                <h3>Restaurant backup notifications</h3>
                <div style={{ display: 'grid', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 750, color: 'rgba(15,23,42,0.85)' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(notifyEnabled)}
                      onChange={(e) => setNotifyEnabled(e.target.checked)}
                      disabled={busy}
                    />
                    Enable backup notifications (email + SMS) when payment is confirmed
                  </label>

                  <div className="pos-field">
                    <div className="pos-field-label">Notification emails</div>
                    <input
                      className="pos-input"
                      type="text"
                      value={notifyEmailsText}
                      onChange={(e) => setNotifyEmailsText(e.target.value)}
                      placeholder="owner@restaurant.com, manager@restaurant.com"
                      disabled={busy}
                    />
                    <div className="field-hint">Comma-separated list</div>
                  </div>

                  <div className="pos-field">
                    <div className="pos-field-label">Notification phone numbers</div>
                    <input
                      className="pos-input"
                      type="text"
                      value={notifyPhonesText}
                      onChange={(e) => setNotifyPhonesText(e.target.value)}
                      placeholder="+14155550123, +14155550124"
                      disabled={busy}
                    />
                    <div className="field-hint">Comma-separated list, E.164 format</div>
                  </div>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button type="button" className="btn-primary" onClick={saveNotifications} disabled={busy}>
                      Save notifications
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {isSquare ? (
              <div className="pos-panel">
                <h3>Square Terminal (card-present)</h3>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div className="field-hint">
                    Square does not show an unpaid order on the Terminal's own screen, so pay-at-store
                    tickets are collected by pushing a checkout to a paired device. Pair once per
                    location and environment.
                  </div>

                  <div className="pos-field">
                    <div className="pos-field-label">Status</div>
                    <div style={{ fontWeight: 900, color: terminalPaired ? '#166534' : 'rgba(15,23,42,0.7)' }}>
                      {terminalPaired ? 'Paired' : 'Not paired'}
                      {terminalPaired && terminalDeviceId ? (
                        <span style={{ fontWeight: 700, color: 'rgba(15,23,42,0.6)' }}>
                          {' '}
                          · {terminalDeviceId}
                        </span>
                      ) : null}
                    </div>
                    <div className="field-hint">
                      {envName} · {selectedLocationId ? `location ${selectedLocationId}` : 'no location selected'}
                    </div>
                  </div>

                  {terminalPairCode ? (
                    <div className="pos-field">
                      <div className="pos-field-label">Enter this code on the Terminal</div>
                      <div
                        style={{
                          fontSize: 32,
                          fontWeight: 900,
                          letterSpacing: 6,
                          color: '#1f1142',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {terminalPairCode}
                      </div>
                      <div className="field-hint">
                        On the Terminal: Settings → Sign in / Use device code. The code is short-lived —
                        start a new one if it expires.
                        {terminalPairPolling ? ' Waiting for the device…' : ''}
                      </div>
                    </div>
                  ) : null}

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={startTerminalPairing}
                      disabled={busy || !posStatus?.connected || !selectedLocationId}
                    >
                      {terminalPaired ? 'Pair a different Terminal' : 'Pair a Terminal'}
                    </button>
                    {terminalPairPolling ? (
                      <button type="button" className="btn-secondary" onClick={stopTerminalPairPolling} disabled={busy}>
                        Stop waiting
                      </button>
                    ) : null}
                    {terminalPaired ? (
                      <button type="button" className="btn-secondary" onClick={unpairTerminal} disabled={busy}>
                        Unpair
                      </button>
                    ) : null}
                  </div>

                  {!posStatus?.connected ? (
                    <div className="field-hint">Connect Square first.</div>
                  ) : !selectedLocationId ? (
                    <div className="field-hint">Select a POS location first.</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {isClover ? (
              <div className="pos-panel">
                <h3>Clover terminal (card-present)</h3>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div className="field-hint">
                    Pay-at-store and dine-in tickets are collected by pushing a charge to a paired
                    Clover terminal (REST Pay Display, cloud connection). Enter the device serial from
                    the Clover device's Setup app (or the Clover web dashboard). The same pairing works
                    on a sandbox Dev Kit and a production terminal — only the connected Clover account
                    differs.
                  </div>

                  <div className="pos-field">
                    <div className="pos-field-label">Status</div>
                    <div style={{ fontWeight: 900, color: cloverTerminalPaired ? '#166534' : 'rgba(15,23,42,0.7)' }}>
                      {cloverTerminalPaired ? 'Paired' : 'Not paired'}
                      {cloverTerminalPaired && cloverTerminalDeviceId ? (
                        <span style={{ fontWeight: 700, color: 'rgba(15,23,42,0.6)' }}>
                          {' '}
                          · {cloverTerminalDeviceId}
                        </span>
                      ) : null}
                    </div>
                    <div className="field-hint">{envName}</div>
                  </div>

                  <div className="pos-field">
                    <div className="pos-field-label">Device serial</div>
                    <input
                      className="pos-input"
                      type="text"
                      value={cloverPairDeviceInput}
                      onChange={(e) => setCloverPairDeviceInput(e.target.value)}
                      placeholder="e.g. C030UQ12345678"
                      disabled={busy}
                    />
                    <div className="field-hint">
                      Find it on the terminal under Setup → Devices, or in the Clover web dashboard.
                    </div>
                  </div>

                  <div className="pos-field">
                    <div className="pos-field-label">Remote App ID (RAID) — optional</div>
                    <input
                      className="pos-input"
                      type="text"
                      value={cloverPairRaidInput}
                      onChange={(e) => setCloverPairRaidInput(e.target.value)}
                      placeholder="Leave blank to use the app default"
                      disabled={busy}
                    />
                    <div className="field-hint">
                      Generated for your Clover app in the Developer Dashboard. The same value is used
                      in sandbox and production. Leave blank if it is set at the tenant level.
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={pairCloverTerminal}
                      disabled={busy || !posStatus?.connected || !String(cloverPairDeviceInput || '').trim()}
                    >
                      {cloverTerminalPaired ? 'Pair a different terminal' : 'Pair a terminal'}
                    </button>
                    {cloverTerminalPaired ? (
                      <button type="button" className="btn-secondary" onClick={unpairCloverTerminal} disabled={busy}>
                        Unpair
                      </button>
                    ) : null}
                  </div>

                  {!posStatus?.connected ? (
                    <div className="field-hint">Connect Clover first.</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="pos-panel">
              <h3>POS taxes</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 750, color: 'rgba(15,23,42,0.85)' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(posTaxEnabled)}
                    onChange={(e) => {
                      const next = Boolean(e.target.checked);
                      setPosTaxEnabled(next);
                      if (next && String(posTaxCollectionMode || '').toUpperCase() === 'OWN' && !String(posTaxRatePercent || '').trim()) {
                        setPosTaxRatePercent('8.25');
                      }
                    }}
                    disabled={busy}
                  />
                  Enable POS tax
                </label>

                {posTaxEnabled ? (
                  <div className="pos-field">
                    <div className="pos-field-label">Tax collection</div>
                    <select
                      className="pos-input"
                      value={posTaxCollectionMode}
                      onChange={(e) => {
                        const next = String(e.target.value || '').toUpperCase();
                        setPosTaxCollectionMode(next);
                        if (next === 'OWN' && !String(posTaxRatePercent || '').trim()) {
                          setPosTaxRatePercent('8.25');
                        }
                      }}
                      disabled={busy}
                    >
                      {!isStripe ? <option value="PROVIDER">Provider collection (Square/Clover)</option> : null}
                      <option value="OWN">Own collection (fixed %)</option>
                    </select>
                    <div className="field-hint">
                      {isStripe
                        ? 'Stripe uses a fixed percentage tax (own collection).'
                        : 'Provider collection uses Square/Clover tax settings. Own collection applies a single percentage tax.'}
                    </div>
                  </div>
                ) : null}

                {posTaxEnabled && String(posTaxCollectionMode || '').toUpperCase() === 'OWN' ? (
                  <div className="pos-field">
                    <div className="pos-field-label">Tax rate (%)</div>
                    <input
                      className="pos-input"
                      type="text"
                      value={posTaxRatePercent}
                      onChange={(e) => setPosTaxRatePercent(e.target.value)}
                      placeholder="8.25"
                      disabled={busy}
                    />
                  </div>
                ) : null}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button type="button" className="btn-primary" onClick={savePosTaxes} disabled={busy || (!posStatus?.connected && !isStripe)}>
                    Save taxes
                  </button>
                </div>
              </div>
            </div>

            {!isStripe ? (
              <div className="pos-panel">
                <h3>Inject test order</h3>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div className="pos-field">
                    <div className="pos-field-label">Sync-enabled product</div>
                    <select
                      className="pos-input"
                      value={selectedProductId}
                      onChange={(e) => setSelectedProductId(e.target.value)}
                      disabled={syncedProducts.length === 0}
                    >
                      {syncedProducts.length === 0 ? (
                        <option value="">No synced products found</option>
                      ) : (
                        syncedProducts.map((p) => (
                          <option key={p.ProductID} value={String(p.ProductID || '')}>
                            {p.ProductName} · {p.Category}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="pos-field">
                    <div className="pos-field-label">Square Catalog Variation ID (override)</div>
                    <input
                      className="pos-input"
                      type="text"
                      value={injectVariationId}
                      onChange={(e) => setInjectVariationId(e.target.value)}
                      placeholder="Leave empty to use selected product"
                    />
                    <div className="field-hint">Select a synced product or override the variation id manually.</div>
                  </div>

                  <details style={{ marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 900, color: '#1f1142' }}>Debug payloads</summary>
                    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                      <div>
                        <div className="pos-field-label">POS status</div>
                        <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{JSON.stringify(posStatus, null, 2)}</pre>
                      </div>
                      {injectResult ? (
                        <div>
                          <div className="pos-field-label">Inject result</div>
                          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{JSON.stringify(injectResult, null, 2)}</pre>
                        </div>
                      ) : null}
                    </div>
                  </details>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
